import { CountryCode, OrganizationCountry } from "../enums/country";
import { getRegionDisplayName } from "../region/display";
import { resolveRegionCode } from "../region/resolve";
const RAFFLE_DISABLED_COUNTRIES = [
    OrganizationCountry.Australia,
    OrganizationCountry.Germany,
    OrganizationCountry.Ireland,
];
const US_RAFFLE_BANNED_REGION_CODES = ["AL", "UT", "HI", "CA", "CT", "GA", "DE"];
export const isRaffleEnabled = (country) => {
    return !RAFFLE_DISABLED_COUNTRIES.includes(country);
};
export const isRaffleStateBanned = (country, region) => {
    if (country !== OrganizationCountry.UnitedStates && country !== CountryCode.US) {
        return false;
    }
    const regionCode = resolveRegionCode(country, region);
    return Boolean(regionCode && US_RAFFLE_BANNED_REGION_CODES.includes(regionCode));
};
const RAFFLE_BUYER_STATE_BAN_START_UTC = new Date("2026-10-13T04:00:00.000Z");
export const isRaffleBuyerStateBanEnforced = (raffleCreatedAtUtc) => !raffleCreatedAtUtc || new Date(raffleCreatedAtUtc).getTime() >= RAFFLE_BUYER_STATE_BAN_START_UTC.getTime();
export const getRaffleRestriction = (country, region) => {
    if (country !== OrganizationCountry.UnitedStates) {
        return { isRestricted: false, regionName: null };
    }
    const regionCode = resolveRegionCode(country, region);
    return {
        isRestricted: isRaffleStateBanned(country, region),
        regionName: regionCode ? getRegionDisplayName(country, regionCode) : null,
    };
};
const US_RAFFLE_MASTER_GUIDE_URL = "https://www.zeffy.com/blog/raffle-laws-by-state";
const CA_RAFFLE_MASTER_GUIDE_URL = "https://www.zeffy.com/blog/canada-raffle-laws";
const US_RAFFLE_COMPLIANCE_CONFIG = {
    AL: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-alabama" },
    AK: { showDisclaimer: false },
    AZ: { showDisclaimer: true },
    AR: { showDisclaimer: true },
    CA: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-california" },
    CO: { showDisclaimer: false, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-in-colorado" },
    CT: { showDisclaimer: false, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-in-connecticut" },
    DE: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/delaware-raffle-laws" },
    DC: { showDisclaimer: true },
    FL: { showDisclaimer: false, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-in-florida" },
    GA: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-georgia" },
    HI: { showDisclaimer: true },
    ID: { showDisclaimer: false },
    IL: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-in-illinois" },
    IN: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-indiana" },
    IA: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-iowa" },
    KS: { showDisclaimer: false },
    KY: { showDisclaimer: true },
    LA: { showDisclaimer: false },
    ME: { showDisclaimer: false },
    MD: { showDisclaimer: true },
    MA: { showDisclaimer: true },
    MI: { showDisclaimer: true },
    MN: { showDisclaimer: true },
    MS: { showDisclaimer: true },
    MO: { showDisclaimer: false },
    MT: { showDisclaimer: true },
    NE: { showDisclaimer: false },
    NV: { showDisclaimer: true },
    NH: { showDisclaimer: true },
    NJ: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-nj" },
    NM: { showDisclaimer: true },
    NY: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-new-york" },
    NC: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-nc" },
    ND: { showDisclaimer: false },
    OH: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-ohio" },
    OK: { showDisclaimer: true },
    OR: { showDisclaimer: true },
    PA: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-in-pennsylvania" },
    RI: { showDisclaimer: true },
    SC: { showDisclaimer: false },
    SD: { showDisclaimer: false },
    TN: { showDisclaimer: true },
    TX: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-texas" },
    UT: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-utah" },
    VT: { showDisclaimer: true },
    VA: { showDisclaimer: true },
    WA: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/nonprofit-raffle-laws-in-washington-state" },
    WV: { showDisclaimer: false },
    WI: { showDisclaimer: true },
    WY: { showDisclaimer: true },
};
const CA_RAFFLE_COMPLIANCE_CONFIG = {
    AB: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/charitable-lottery-regulations-alberta" },
    BC: {
        showDisclaimer: true,
        learnMoreUrl: "https://www.zeffy.com/blog/charitable-gaming-regulations-british-columbia",
    },
    NS: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/charitable-lottery-regulations-nova-scotia" },
    ON: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/charitable-lottery-regulations-ontario" },
    QC: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/charitable-lottery-regulations-quebec" },
    SK: { showDisclaimer: true, learnMoreUrl: "https://www.zeffy.com/blog/charitable-lottery-regulations-saskatchewan" },
};
export const getRaffleComplianceDisclaimer = (country, region) => {
    if (country !== OrganizationCountry.UnitedStates && country !== OrganizationCountry.Canada) {
        return null;
    }
    const variant = country === OrganizationCountry.UnitedStates ? "US" : "CA";
    const masterGuideUrl = variant === "US" ? US_RAFFLE_MASTER_GUIDE_URL : CA_RAFFLE_MASTER_GUIDE_URL;
    const regionCode = resolveRegionCode(country, region);
    const config = variant === "US"
        ? US_RAFFLE_COMPLIANCE_CONFIG[regionCode]
        : CA_RAFFLE_COMPLIANCE_CONFIG[regionCode];
    if (regionCode && config?.showDisclaimer === false) {
        return null;
    }
    return { variant, regionCode, learnMoreUrl: config?.learnMoreUrl ?? masterGuideUrl };
};
